import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import axios from 'axios';
import { debounce } from 'lodash';
import { Row, Col, Button, Form, Card, Space, Modal, notification } from 'antd';
import FormInput from '../../../components/FormInput';
import Firebase from '../../../lib/Firebase';
import FormSelect from '../../../components/FormSelect';
import FormDatePicker from '../../../components/FormDatePicker';
import FormPlacesAutocomplete from '../../../components/FormPlacesAutocomplete';
import { getAddressDetails } from '../../../lib/googleMapApi';

dayjs.extend(utc);
dayjs.extend(timezone);

const diningSites = [
  {
    name: "Daytona Beach Neighborhood Dining Site",
    lines: [
      "John H. Dickerson Community Center",
      "Mickel A. Brown, Sr. Activity Building",
      "308 S. Dr. Martin Luther King Jr. Boulevard",
      "Daytona Beach, FL 32114"
    ]
  },
  {
    name: "Ormond Beach Neighborhood Dining Site",
    lines: [
      "351 Andrews Street",
      "Ormond Beach, FL 32174"
    ]
  },
  {
    name: "Port Orange Neighborhood Dining Site",
    lines: [
      "4790 S. Ridgewood Avenue",
      "Port Orange, FL 32119"
    ]
  },
  {
    name: "New Smyrna Beach (NSB) Neighborhood Dining Site",
    lines: [
      "NSB Live Oak Cultural Center",
      "1050 Live Oak Street",
      "New Smyrna Beach, FL 32168"
    ]
  },
  {
    name: "Deltona Neighborhood Dining Site",
    lines: [
      "The Center at Deltona",
      "1640 Dr. Martin Luther King Boulevard",
      "Deltona, FL 32725"
    ]
  },
  {
    name: "Orange City Neighborhood Dining Site",
    lines: [
      "City of Orange City",
      "Wava Hall Senior Center",
      "200 N. Holly Avenue",
      "Orange City, FL 32763"
    ]
  },
  {
    name: "Deland Neighborhood Dining Site",
    lines: [
      "Tomoka Christian Church",
      "1101 East Plymouth Avenue",
      "Deland, FL 32724"
    ]
  }
];

const midpoint = Math.ceil(diningSites.length / 2);
const leftColumn = diningSites.slice(0, midpoint);
const rightColumn = diningSites.slice(midpoint);

const titleStyle = {
	fontFamily: "Roboto",
	fontSize: 20,
	color: "#4A525F",
	fontWeight: 600,
};

const displayNotification = (description, cb = () => {}) => {
	notification.warning({
		message: "New Reservation Alert",
		description,
	});

	cb(false);

	return;
};

const formatPhoneNumber = (value) => {
	if (!value) return null;
	const extractNumber = value.replace(/\D/g, "");
	if (!extractNumber) return null;
	const first3String = extractNumber.substring(0, 3);
	const second3String = extractNumber.substring(3, 6);
	const remString = extractNumber.substring(6, 10);
	return `${first3String ? `(${first3String})` : ""}${second3String ? ` ${second3String}` : ""}${remString ? `-${remString}` : ""}`;
};

const formatAddress = async (address, type = "pickup", cb) => {
	let errorMsg = null;
	let result = {};

	try {
		result = await getAddressDetails(address);
	} catch(err) {
		errorMsg = "Please enter correct address.";
	}

	if (result?.addressLine === "") {
		errorMsg = `Please enter correct address. Invalid ${type} address line.`;
	} else if (result?.city === "") {
		errorMsg = `Please enter correct address. Invalid ${type} city`;
	} else if (result?.state === "") {
		errorMsg = `Please enter correct address. Invalid ${type} state.`;
	} else if (result?.zipcode === "") {
		errorMsg = `Please enter correct address. Invalid ${type} zip code.`;
	} else if (result?.latitude === 0) {
		errorMsg = `Please enter correct address. Invalid ${type} latitude value from address.`;
	} else if (result?.longitude === 0) {
		errorMsg = `Please enter correct address. Invalid ${type} longitude value from address.`;
	} else {
		return result;
	}

	displayNotification(errorMsg, cb);

	return null;
}

function CCTMealTransportPortal({
	fleetData,
	facilityId,
	portalId,
	spanWindow
}) {
	const [form] = Form.useForm();
	const [tripFrom, setTripFrom] = useState("Somebody else");
	const [tripLeg, setTripLeg] = useState(null);
	const [isFieldReset, setIsFieldReset] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const programTimezone = fleetData?.program?.timezone || null
	const mobilityAssistanceList = fleetData?.mobility_assistance || [];

	// watched forms
	const userFirstname = Form.useWatch('user_firstname', form);
	const userLastname = Form.useWatch('user_lastname', form);
	const userPhone = Form.useWatch('user_phone', form);
	const userEmail = Form.useWatch('user_email', form);

	const onFinish = debounce(async (values) => {
		try {
			setIsSubmitting(true);

			const {
				pickup_address,
				user_phone,
				rider_phone,
			} = values;

			if (user_phone && user_phone.length !== 14) {
				displayNotification("Customer's phone format is invalid.", setIsSubmitting);

				return;
			}

			if (rider_phone && rider_phone.length !== 14) {
				displayNotification("Rider's phone format is invalid.", setIsSubmitting);

				return;
			}


			const formattedPickupAddress = await formatAddress(pickup_address, "pickup", setIsSubmitting);

			if (formattedPickupAddress) {
				const pickupFullAddress = `${formattedPickupAddress.addressLine}, ${formattedPickupAddress.city}, ${formattedPickupAddress.state}, ${formattedPickupAddress.zipcode}`;
				const maObj = mobilityAssistanceList.find((obj) => obj.type === values.mobility_assistance);

				values.public_portal_id = portalId;
				values.pickup_date = values.pickup_date && dayjs(values.pickup_date).format("dddd, MMMM D, YYYY");
				values.pickup_address = formattedPickupAddress;
				values.program_id = fleetData.program.id;
				values.fleet_id = fleetData.id;
				values.mobility_assistance = values.mobility_assistance === "None"
					? [] : [values.mobility_assistance];
				values.office_note =
					"Submitted through Portal by:\n"
					+ `First Name: ${(values.user_firstname || "").trim()}\n`
					+ `Last Name: ${(values.user_lastname || "").trim()}\n`
					+ `Mobile Phone: ${values.user_phone}\n`
					+ "\n"
					+ "Rider information:\n"
					+ `First Name: ${(values.rider_firstname || "").trim()}\n`
					+ `Last Name: ${(values.rider_lastname || "").trim()}\n`
					+ `Mobile Phone: ${values.rider_phone || ""}\n`
					+ "\n"
					+ `${values.pickup_date ? `Pickup Date: ${dayjs(values.pickup_date).format("dddd, MMMM D, YYYY")}\n` : ""}`
					+ `${maObj ? `Mobility: ${maObj.label || ""}\n` : ""}`
					+ `${values.additional_passenger ? `Additional Passenger: ${values.additional_passenger || ""}\n` : ""}`
					+ `Pickup Address: ${pickupFullAddress}\n`
					+ `${values.date_for_cancel_or_change ? `Specified Date(s)/Week for Cancellation or Change: ${values.date_for_cancel_or_change || ""}\n` : ""}`
					+ `${values.return_time ? `Return trip time: ${values.return_time || ""}\n` : ""}`
					+ "\n"
					+ `Notes: ${values.office_note || ""}\n`
				;

				// Default payment method
				values.payment_method = 1;
				values.facility_portal_id = facilityId;
				values.insurance_id = fleetData?.facility.id;

				const functionName = "v1_webapp_public_create_new_reservation";
				const appCheckToken = await Firebase.getAppCheckToken();

				await axios.post(`${process.env.REACT_APP_DUET_SERVER_URL}/${functionName}`, {
					...values
				}, {
					headers: {
						'X-Parse-Application-Id': process.env.REACT_APP_DUET_APP_ID,
						'X-Firebase-AppCheck': appCheckToken,
						'Content-Type': 'application/json'
					},
				});

				form.resetFields();
				setIsFieldReset(true);
				setIsSubmitting(false);
				setTripFrom("Somebody else");

				notification.success({
					message: "New Reservation Success",
					description: 'The reservation is successfully created.',
				});
			}
		} catch (error) {
			notification.error({
				message: "New Reservation Error",
				description: error?.response?.data
				? error.response.data.error.message
				: error.toString(),
			});
			setIsSubmitting(false);
		}
	}, 1000);
	
	const onValuesChange = (data) => {
		const { trip_from, trip_leg } = data;

		if (trip_from) {
			setTripFrom(trip_from);
		}

		if (trip_leg) {
			setTripLeg(trip_leg);
		}
	};

	const cancelModal = () => {
		Modal.confirm({
		  title: 'Do you want to cancel this reservation?',
		  content: (
			<p>All data entered into the fields will be reset.</p>
		  ),
		  onOk: () => {
			form.resetFields();
			setIsFieldReset(true);
			setTripFrom("Somebody else");
		  },
		});
	};

	useEffect(() => {
		if (tripFrom === "Myself" && !isSubmitting) {
			if (userFirstname
				|| userLastname
				|| userPhone
				|| userEmail
				|| userFirstname === ""
				|| userLastname === ""
				|| userPhone === ""
				|| userEmail === ""
			) {
				form.setFieldsValue({
					rider_firstname: userFirstname,
					rider_lastname: userLastname,
					rider_phone: userPhone,
					rider_email: userEmail,
				});
			}
		}
	}, [form, tripFrom, userFirstname, userLastname, userPhone, userEmail, isSubmitting])

	useEffect(() => {
		if (tripFrom === "Somebody else") {
			form.setFieldsValue({
				rider_firstname: null,
				rider_lastname: null,
				rider_phone: null,
				rider_email: null,
			});
		}
	}, [form, tripFrom]);

	useEffect(() => {
		if (tripLeg === 1) {
			form.setFieldsValue({
				return_date: null,
				return_time: null,
			})
		}
	}, [tripLeg, form]);

	return (
		<div
			style={{
				textAlign: "left",
				fontFamily: "Roboto",
			}}
		>
			<div className="fleet-title">
				<span
					style={{
						display: "block",
						fontSize: 26,
						fontWeight: 800,
						marginBottom: 20,
						lineHeight: "normal"
					}}
				>
					{fleetData?.name}
				</span>
				<span
					style={{
						display: `${(fleetData?.phone || fleetData?.email) ? "inline-block" : "none"}`,
						fontSize: 18,
						fontWeight: 400,
						lineHeight: 1.5
					}}
				>
					Phone: {"(850) 900-0228"}, Email: info@cctransportations.com
				</span>
			</div>
			<Card
				title={
					<Row style={{margin: "20px 0"}}>
						<Col span={24} style={{marginBottom: 5}}>
							<span style={{...titleStyle, fontWeight: 700}}>
								Meal Transport Rider Intake Information
							</span>
						</Col>
						<Col span={24} style={{fontSize: 14, display: "contents"}}>
							<span style={{color: "#9398A0"}}>
								Riders should be ready to transport up to 30 minutes before their scheduled time.
								The driver will not be permitted to wait beyond the 10-minute window.
							</span>
						</Col>
					</Row>
				}
			>
				<Form
					form={form}
					name="reservation-form"
					onFinish={onFinish}
					onValuesChange={onValuesChange}
					autoComplete="off"
				>
					<div style={{marginBottom: 20}}>
						<span style={{ ...titleStyle, fontSize: 18 }}>
							Your Contact Information
						</span>
						<Card
							size="small"
							className="description-container"
						>
							<Row gutter={[20, 0]}>
								<Col span={spanWindow}>
									<FormInput
										name="user_firstname"
										label="First Name"
										rules={[{
											pattern: /^[A-Za-z\s]*$/,
											message: "Please input letters only."
										}]}
										required
									/>
								</Col>
								<Col span={spanWindow}>
									<FormInput
										name="user_lastname"
										label="Last Name"
										rules={[{
											pattern: /^[A-Za-z\s]*$/,
											message: "Please input letters only."
										}]}
										required
									/>
								</Col>
								<Col span={spanWindow}>
									<FormInput
										name="user_phone"
										label="Phone"
										setValue={(data) => form.setFieldValue("user_phone", data)}
										onBlur={(e) => {
											const formatted = formatPhoneNumber(e.target.value);
											form.setFieldValue("user_phone", formatted);
										}}
										required
									/>
								</Col>
								<Col span={spanWindow}>
									<FormSelect
										name="trip_from"
										label="Who is this form for?"
										options={[
											{ value: "Myself", label: "Myself" },
											{ value: "Somebody else", label: "Somebody else" },
										]}
										required
									/>
								</Col>
							</Row>
						</Card>
					</div>
					<div style={{marginBottom: 20}}>
						<span style={{ ...titleStyle, fontSize: 18 }}>
							Rider
						</span>
						<Card
							size="small"
							className="description-container"
						>
							<Row gutter={[20, 0]}>
								<Col span={spanWindow}>
									<FormInput
										name="rider_firstname"
										label="First Name"
										rules={[{
											pattern: /^[A-Za-z\s]*$/,
											message: "Please input letters only."
										}]}
										required
										disabled={tripFrom === "Myself"}
									/>
								</Col>
								<Col span={spanWindow}>
									<FormInput
										name="rider_lastname"
										label="Last Name"
										rules={[{
											pattern: /^[A-Za-z\s]*$/,
											message: "Please input letters only."
										}]}
										required
										disabled={tripFrom === "Myself"}
									/>
								</Col>
								<Col span={spanWindow}>
									<FormInput
										name="rider_phone"
										label="Phone"
										setValue={(data) => form.setFieldValue("rider_phone", data)}
										onBlur={(e) => {
											const formatted = formatPhoneNumber(e.target.value);
											form.setFieldValue("rider_phone", formatted);
										}}
										disabled={tripFrom === "Myself"}
										required
									/>
								</Col>
							</Row>
						</Card>
					</div>
					<div style={{marginBottom: 40}}>
						<span style={{ ...titleStyle, fontSize: 18 }}>
							Trip Information
						</span>
						<Card
							size="small"
							className="description-container"
						>
							<Row gutter={[20, 0]}>
								<Col span={spanWindow}>
									<FormSelect
										name="mobility_assistance"
										label="Mobility Assistance"
										options={[{
											value: "None",
											label: "None"
										}].concat(mobilityAssistanceList
											.map((ma) => {
												ma.label = ma.type === 0 ? "Able to walk" : ma.label;
												return {value: ma.type, ...ma};
											})
											.sort((a, b) => a.value - b.value))}
										required
									/>
								</Col>
								<Col span={spanWindow}>
									<FormSelect
										name="additional_passenger"
										label="Additional Passenger"
										options={[
											{value: 0, label: "None"},
											{value: 1, label: "1"},
											{value: 2, label: "2"},
											{value: 3, label: "3"},
										]}
										required
									/>
								</Col>
								<Col span={24}>
									<FormPlacesAutocomplete
										name="pickup_address"
										label="Address for Pickup"
										isReset={isFieldReset}
										setIsReset={setIsFieldReset}
										setValue={(data) => form.setFieldValue("pickup_address", data)}
										required
									/>
								</Col>
								<Col span={24}>
									<FormSelect
										name="trip_leg"
										label="Are you booking a new transport, or changing an existing transport with us?"
										options={[
											{value: 1, label: "Cancel or change my existing transport(s)"},
											{value: 2, label: "Book a new transport"},
										]}
										required
									/>
								</Col>
								<Col span={24} style={{display: tripLeg === 2 ? "inline-block" : "none"}}>
									<FormDatePicker
										name="pickup_date"
										label="Pickup Date"
										disabled={tripLeg === 1}
										disabledDate={(current) => {
											const currentDate = dayjs(current).tz(programTimezone);
											return  currentDate.isBefore(dayjs.tz(new Date(), programTimezone).startOf("day"));
										}}
										required={tripLeg === 2}
									/>
								</Col>
								<Col span={24} style={{display: tripLeg === 1 ? "inline-block" : "none"}}>
									<FormInput
										name="date_for_cancel_or_change"
										label="Please specify the date(s) or week of the transports that you want to cancel or change?"
										disabled={tripLeg === 2}
										required={tripLeg === 1}
									/>
								</Col>
								<Col span={24}>
									<FormInput
										type="text-area"
										name="office_note"
										label="Note, anything else that we need to know about this trip?"
									/>
								</Col>
							</Row>
						</Card>
						<span
							style={{
								display: "block",
								color: "#9398A0",
								fontSize: 14,
								marginTop: "5px"
							}}
						>
							To cancel or alter subscription transports, please call (407) 718-0061. Meal Sites are listed below.
						</span>
					</div>
					<Form.Item
						style={{
							textAlign: "right",
							marginBottom: 26,
						}}
					>
						<Space>
							<Button
								style={{
									borderRadius: 3,
									fontWeight: 600,
									backgroundColor: "#D7DCE5",
									color: "#4A525F"
								}}
								size="large"
								htmlType="button"
								onClick={cancelModal}
							>
								Cancel
							</Button>
							<Button
								style={{
									backgroundColor: "#118BF0",
									color: "#FFFFFF",
									borderRadius: 3,
									fontWeight: 600,
								}}
								size="large"
								htmlType="submit"
								loading={isSubmitting}
							>
								Submit
							</Button>
						</Space>
					</Form.Item>
				</Form>
			</Card>
			<div className="meal-text" style={{ display: "block", width: "100%", fontWeight: 500, color: "#4A525F"}}>
				<Row gutter={32} style={{ marginTop: 40 }}>
					<Col span={24}>
						<span className="meal-text-header">
							Meal transportation will be provided to the closest meal location to your
							home, i.e., pickup address.
						</span>
					</Col>
					<Col span={24} style={{marginTop: 10, marginBottom: 20}}>
						<span className="meal-text-header" style={{ fontWeight: 700 }}>Neighborhood Dining Sites</span>
						<br/>
						<span>(Subject to change, but will always be in Volusia County)</span>
					</Col>
					<Col xs={24} md={12}>
						{leftColumn.map((site, index) => (
							<div key={index} style={{ marginBottom: 20 }}>
								<div style={{ display: "flex" }}>
									<span style={{ fontWeight: 700, marginRight: 8 }}>{index + 1}.</span>
									<span style={{ fontWeight: 700 }}>{site.name}</span>
								</div>

								<div style={{ paddingLeft: 22 }}>
									{site.lines.map((line, i) => (
									<div key={i}>{line}</div>
									))}
								</div>
							</div>
						))}
					</Col>
					<Col xs={24} md={12}>
						{rightColumn.map((site, index) => {
							const number = midpoint + index + 1;
							return (
								<div key={index} style={{ marginBottom: 20 }}>
									<div style={{ display: "flex" }}>
										<span style={{ fontWeight: 700, marginRight: 8 }}>{number}.</span>
										<span style={{ fontWeight: 700 }}>{site.name}</span>
									</div>

									<div style={{ paddingLeft: 22 }}>
									{site.lines.map((line, i) => (
										<div key={i}>{line}</div>
									))}
									</div>
								</div>
							);
						})}
					</Col>
				</Row>
			</div>
		</div>
	);
}

export default CCTMealTransportPortal;